import { useEffect } from 'react';

import { useFormikContext } from 'formik';

import { ShippingSpeed } from 'constants/enums';
import { useShippingOptions } from 'hooks';
import type { IShippingInfo } from 'pages/Dashboard/Onboard/Onboard';
import { toDeviceOrderShippingInfo } from 'utils/Address';

import ShippingSpeedSelector from './ShippingSpeedSelector';

/** The three fields a form must hold to host the selector. */
export interface IShippingSpeedFormValues {
  shippingInfo: IShippingInfo;
  shippingSpeed: ShippingSpeed;
  /** The chosen speed's window, kept so Review can repeat it without asking Amazon again. */
  shippingSpeedEstimate: IDeliveryWindow | null;
}

interface IShippingSpeedFieldProps {
  gid: string;
  /** What the order would contain; the preview is requested for these. */
  items: IBulkDeviceOrderItem[];
  /** False when the cart has no Amazon-fulfilled SKU: nothing is rendered or requested. */
  enabled: boolean;
  bulk?: boolean;
}

const sameWindow = (a: IDeliveryWindow | null, b: IDeliveryWindow | null) =>
  (a === null && b === null) || (a?.start === b?.start && a?.end === b?.end);

/**
 * The shipping-speed section bound to a Formik form: fetches the preview for the address being
 * typed, renders the cards, and keeps `shippingSpeed` / `shippingSpeedEstimate` in step with it.
 *
 * Shared by the order wizard and the replacement modal, which both hold `shippingInfo` under the
 * same name. Expedited falls back to Standard the moment the preview says the address cannot have
 * it, so the form never submits a speed the cards no longer offer. The estimate is only written
 * once an answer is in: while a preview is pending the last answered window stays, so Review still
 * has one if Next is pressed mid-debounce or the step is revisited.
 */
const ShippingSpeedField = ({ gid, items, enabled, bulk = false }: IShippingSpeedFieldProps) => {
  const { values, setFieldValue } = useFormikContext<IShippingSpeedFormValues>();
  const { shippingInfo, shippingSpeed, shippingSpeedEstimate } = values;

  const { options, loading } = useShippingOptions({
    gid,
    address: shippingInfo.address,
    shippingInfo: toDeviceOrderShippingInfo(shippingInfo),
    items,
    enabled
  });

  const selected = options?.find((option) => option.speed === shippingSpeed);
  const expeditedBlocked =
    shippingSpeed === ShippingSpeed.EXPEDITED &&
    (bulk || !enabled || selected?.available === false);
  const answered = !loading && options !== undefined;
  const estimate = answered ? selected?.estimatedDelivery ?? null : null;

  useEffect(() => {
    if (expeditedBlocked) setFieldValue('shippingSpeed', ShippingSpeed.STANDARD, false);
  }, [expeditedBlocked, setFieldValue]);

  useEffect(() => {
    if (!answered || sameWindow(estimate, shippingSpeedEstimate)) return;
    setFieldValue('shippingSpeedEstimate', estimate, false);
  }, [answered, estimate, shippingSpeedEstimate, setFieldValue]);

  if (!enabled) return null;

  return (
    <ShippingSpeedSelector
      options={options}
      loading={loading}
      value={shippingSpeed}
      onChange={(speed) => setFieldValue('shippingSpeed', speed, false)}
      bulk={bulk}
    />
  );
};

export default ShippingSpeedField;
